import {API_ENDPOINTS, LOGIN_PATH} from '../constants';
import * as Cookies from 'js-cookie';
import {AUTH_COOKIE_NAME} from '../constants';
import {isEmpty} from 'src/util';

export interface IAlerts {
    messages: number;
    partners: number;
    info: number;
}

export interface IAddress {
    firstLine: string;
    secondLine: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface IPhone {
    mobile: string;
    work: string;
}

// TODO nomenclature  - an org account has an owner and an organization
// whereas this is a 'user' - this is potentially confusing
export interface IAccount {
    phone: IPhone;
    address: IAddress;
    organization: string;
    fullName: string;
    email: string;
}

export interface IOrganization {
    address: IAddress;
    email: string;
    website: string;
    orgName: string;
    phone: string;
    avatar: string;
}

export interface IAccountDetails {
    created: number;
    modified: number;
    name: string;
    organization: IOrganization;
    owner: IAccount;
    subscriptionLevel: number;
}

export interface IAccountSearchResult {
    zipCode: string;
    website: string;
    address: string;
    city: string;
    organization: string;
    name: string;
    email: string;
}

export interface ILogin {
    accountName: string;
    fullName: string;
    userName: string;
}

export const getAuthToken: () => string | undefined = () => {
    return Cookies.get(AUTH_COOKIE_NAME);
};

export const createAccount = async (
    owner: IAccount,
    organization: IOrganization
): Promise<{success: boolean; message?: string}> => {
    try {
        const request = await fetch(API_ENDPOINTS.account(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({organization, owner}),
        });
        if (request.ok) {
            return {success: true};
        } else {
            return {success: false, message: await request.text()};
        }
    } catch (ex) {
        return {success: false, message: ex.message};
    }
};

export const login = async (userName: string, password: string) => {
    try {
        const request = await fetch(API_ENDPOINTS.logins(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({userName, password}),
        });
        if (request.ok) {
            const x = await request.json();
            if (x.message === 'ok') {
                Cookies.set(AUTH_COOKIE_NAME, x.jwt);
                return {success: true};
            } else {
                return {success: false, message: x.message};
            }
        } else {
            return {success: false, message: 'Invalid login'};
        }
    } catch (ex) {
        return {
            success: false,
            message: 'There was an error connecting to the server. Please try again.',
        };
    }
};

export const activate = async (token: string) => {
    try {
        const request = await fetch(API_ENDPOINTS.activateAccount(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({token}),
        });
        if (request.ok) {
            return {success: true};
        } else {
            return {success: false, message: await request.text()};
        }
    } catch (ex) {
        return {
            success: false,
            message: 'There was an error connecting to the server. Please try again later.',
        };
    }
};

export const passwordResetRequest = async (userName: string) => {
    try {
        const formData = new URLSearchParams();
        formData.append('user_name', userName);
        const request = await fetch(API_ENDPOINTS.passwordResetRequest(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
        });
        if (request.ok) {
            const x = await request.json();
            if (x.status === 'ok') {
                return {success: true};
            } else {
                return {success: false, message: x.exception};
            }
        } else {
            return {success: false, message: await request.text()};
        }
    } catch (ex) {
        return {
            success: false,
            message: 'There was an error connecting to the server. Please try again later.',
        };
    }
};

export const passwordReset = async (token: string, password: string) => {
    try {
        const request = await fetch(API_ENDPOINTS.passwordReset(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({token, password}),
        });
        if (request.ok) {
            const x = await request.json();
            if (x.status === 'ok') {
                return {success: true};
            } else {
                return {success: false, message: x.exception};
            }
        } else {
            return {success: false, message: await request.text()};
        }
    } catch (ex) {
        return {
            success: false,
            message: 'There was an error connecting to the server. Please try again later.',
        };
    }
};

export const logout = async () => {
    Cookies.remove(AUTH_COOKIE_NAME);
};

export const getOrganization = async (): Promise<IOrganization | null> => {
    const request = await fetch(API_ENDPOINTS.organization(), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    if (request.ok) {
        const x = await request.json();
        return x;
    }
    return null;
};

export const updateOrganization = async (organization: IOrganization): Promise<boolean> => {
    const request = await fetch(API_ENDPOINTS.updateOrganization(), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(organization),
    });
    return request.ok;
};

export const updateAccount = async (owner: IAccount): Promise<boolean> => {
    const request = await fetch(API_ENDPOINTS.updateOwner(), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(owner),
    });
    return request.ok;
};

export const getPendingPartnersCount = async () => {
    const request = await fetch(API_ENDPOINTS.pendingPartnersCount(), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    if (request.ok) {
        const x: object = await request.json();
        // @ts-ignore
        return x.count ? x.count : 0;
    }
    return 0;
};

export const search = async (
    searchTerm: string,
    maxResults: number = 0,
    clientsOnly: boolean = false
): Promise<{success: boolean; results?: IAccountSearchResult[]}> => {
    // TODO has to be a nicer way to handle wildcard vs insufficient search term
    if (searchTerm.length < 2 && searchTerm !== '*') return {success: true, results: []};
    const request = await fetch(API_ENDPOINTS.accountSearch(searchTerm, maxResults, clientsOnly), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    if (request.ok) {
        const j = await request.json();
        const results = j.rows;
        return {success: true, results};
    } else {
        return {success: false};
    }
};

export const getAccountDetails = async (): Promise<{
    success: boolean;
    accountDetails?: IAccountDetails;
}> => {
    const request = await fetch(API_ENDPOINTS.accountDetails(), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    if (request.ok) {
        const j = await request.json();
        return {success: true, accountDetails: j};
    } else {
        window.location.href = LOGIN_PATH;
        return {success: false};
    }
};

export const getAccountGoogleAnalyticsId = async (): Promise<{
    success: boolean;
    id?: string;
}> => {
    const request = await fetch(API_ENDPOINTS.accountGoogleAnalyticsIDs(), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    if (request.ok) {
        const j = await request.json();
        return {success: true, id: j.analytics};
    } else {
        // window.location.href = LOGIN_PATH;
        return {success: false};
    }
};

export const updateAccountGoogleAnalyticsId = async (analytics: string): Promise<boolean> => {
    const request = await fetch(API_ENDPOINTS.accountGoogleAnalyticsIDs(), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({analytics}),
    });
    return request.ok;
};

export const getLoginDetails = async (): Promise<{
    success: boolean;
    loginDetails?: ILogin;
}> => {
    const request = await fetch(API_ENDPOINTS.logins(), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    if (request.ok) {
        const j = await request.json();
        // TODO shortcut - we only have one login for now, so take first one
        // and if it returns, we should always have at least ourselves
        return {success: true, loginDetails: isEmpty(j) ? null : j[0]};
    } else {
        return {success: false};
    }
};



// WEBPACK FOOTER //
// ./src/services/account.ts